<!--
  MealTypesManager.vue

  Manage meal types and the time of day each is associated with. The time set here
  is what auto-fills the Time field when planning a meal of that type.
  Lives in the Preferences tab of the user profile.

  Editing model: all rows are edited locally and committed together via "Save
  changes". Server refetches merge in without clobbering unsaved edits, so you can
  tweak several rows one by one and save them in a single batch without losing any.
-->
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_MEAL_TYPES } from '../../graphql/queries'
import { CREATE_MEAL_TYPE, UPDATE_MEAL_TYPE, DELETE_MEAL_TYPE } from '../../graphql/mutations'
import { showSuccess, showError } from '../../utils/errorHandling'
import type { MealType } from '../../graphql/types'
import BaseButton from '../BaseButton.vue'
import ConfirmDialog from '../ConfirmDialog.vue'
import TimePicker from '../common/TimePicker.vue'

interface Row {
  key: string // stable local key
  id: string | null // null = new, not yet saved
  name: string
  time: string // "HH:MM" or ""
}

const { result, loading, refetch } = useQuery(GET_MEAL_TYPES)

const rows = ref<Row[]>([])
// Server snapshot per id, used to detect which rows the user has edited.
const originals = ref<Map<string, { name: string; time: string }>>(new Map())

let uid = 0
const nextKey = () => `new-${uid++}`

const isDirty = (row: Row): boolean => {
  if (row.id === null) return row.name.trim() !== '' || row.time !== ''
  const orig = originals.value.get(row.id)
  if (!orig) return false
  return row.name !== orig.name || row.time !== orig.time
}

const dirtyRows = computed(() => rows.value.filter(isDirty))
const hasChanges = computed(() => dirtyRows.value.length > 0)

/**
 * Sync from the server, but preserve any locally-edited or newly-added rows so a
 * refetch (after a save/delete) never discards work in progress.
 */
watch(
  () => result.value?.mealTypes as MealType[] | undefined,
  (mealTypes) => {
    const server = mealTypes ?? []
    originals.value = new Map(server.map((t) => [t.id, { name: t.name, time: t.time ?? '' }]))

    const localById = new Map(rows.value.filter((r) => r.id).map((r) => [r.id as string, r]))
    const merged: Row[] = server.map((t) => {
      const local = localById.get(t.id)
      // Keep the user's in-progress edit if it differs from the fresh server value.
      if (local && (local.name !== t.name || local.time !== (t.time ?? ''))) return local
      return { key: t.id, id: t.id, name: t.name, time: t.time ?? '' }
    })
    // Keep unsaved new rows that aren't on the server yet.
    const newRows = rows.value.filter((r) => r.id === null)
    rows.value = [...merged, ...newRows]
  },
  { immediate: true },
)

const { mutate: createMealType } = useMutation(CREATE_MEAL_TYPE)
const { mutate: updateMealType } = useMutation(UPDATE_MEAL_TYPE)
const { mutate: deleteMealType } = useMutation(DELETE_MEAL_TYPE)

const saving = ref(false)

const addRow = () => {
  rows.value.push({ key: nextKey(), id: null, name: '', time: '' })
}

const saveAll = async () => {
  const toSave = dirtyRows.value
  // Every row we're about to save needs a name.
  if (toSave.some((r) => !r.name.trim())) {
    showError(new Error('Each meal type needs a name before saving.'), 'save meal types')
    return
  }

  const toCreate = toSave.filter((r) => r.id === null)
  const toUpdate = toSave.filter((r) => r.id !== null)
  if (toCreate.length === 0 && toUpdate.length === 0) return

  saving.value = true
  const createdKeys: string[] = []
  let failures = 0
  try {
    const createResults = await Promise.allSettled(
      toCreate.map((r) => createMealType({ name: r.name.trim(), time: r.time || null })),
    )
    createResults.forEach((res, i) => {
      if (res.status === 'fulfilled') createdKeys.push(toCreate[i].key)
      else failures++
    })

    const updateResults = await Promise.allSettled(
      toUpdate.map((r) => updateMealType({ id: r.id, data: { name: r.name.trim(), time: r.time || null } })),
    )
    updateResults.forEach((res) => {
      if (res.status === 'rejected') failures++
    })
  } finally {
    saving.value = false
  }

  // Drop the new rows that were created so the refetch doesn't duplicate them.
  if (createdKeys.length) rows.value = rows.value.filter((r) => !createdKeys.includes(r.key))
  await refetch()

  if (failures > 0) showError(new Error(`${failures} change(s) could not be saved.`), 'save meal types')
  else showSuccess('Meal types saved.')
}

const discard = () => {
  rows.value = Array.from(originals.value.entries()).map(([id, o]) => ({
    key: id,
    id,
    name: o.name,
    time: o.time,
  }))
}

// ── Deletion ──
const pendingDelete = ref<Row | null>(null)
const deleting = ref(false)

const removeRow = (row: Row) => {
  if (row.id === null) {
    // Unsaved new row — just drop it, no server call.
    rows.value = rows.value.filter((r) => r.key !== row.key)
  } else {
    pendingDelete.value = row
  }
}

const confirmDelete = async () => {
  if (!pendingDelete.value) return
  deleting.value = true
  try {
    await deleteMealType({ id: pendingDelete.value.id })
    showSuccess(`"${pendingDelete.value.name}" deleted.`)
    pendingDelete.value = null
    await refetch()
  } catch (e) {
    showError(e, 'delete meal type')
  } finally {
    deleting.value = false
  }
}

// Warn before a full page reload if there are unsaved edits.
const onBeforeUnload = (e: BeforeUnloadEvent) => {
  if (hasChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}
onMounted(() => window.addEventListener('beforeunload', onBeforeUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', onBeforeUnload))
</script>

<template>
  <div class="bg-white border border-gray-200 rounded-lg p-6">
    <div class="mb-5">
      <h2 class="text-section-header">Meal types</h2>
      <p class="text-caption mt-1">
        Give each meal type a time of day. When you plan a meal of that type, its time fills in
        automatically (you can still change it). Edit as many as you like, then save them all at once.
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading && rows.length === 0" class="text-center py-8 text-gray-500 text-sm">
      Loading meal types…
    </div>

    <div v-else>
      <ul v-if="rows.length" class="flex flex-col gap-3">
        <li v-for="row in rows" :key="row.key" class="flex items-end gap-3 flex-wrap sm:flex-nowrap">
          <div class="flex flex-col gap-1 flex-1 min-w-[140px]">
            <label class="text-xs font-medium text-gray-500 flex items-center gap-1.5">
              Name
              <span v-if="isDirty(row)" class="dirty-badge">{{ row.id === null ? 'New' : 'Edited' }}</span>
            </label>
            <input v-model="row.name" type="text" placeholder="e.g. Breakfast"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-sm" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-500">Time</label>
            <TimePicker v-model="row.time" />
          </div>
          <button type="button"
            class="p-2 mb-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete meal type" @click="removeRow(row)">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </li>
      </ul>

      <p v-else class="text-sm text-gray-500">No meal types yet. Add your first one below.</p>

      <!-- Add row -->
      <button type="button"
        class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
        @click="addRow">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14" />
        </svg>
        Add a meal type
      </button>

      <!-- Action bar (only when there are unsaved changes) -->
      <div v-if="hasChanges" class="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-100 flex-wrap">
        <p class="text-sm text-gray-500">
          You have <strong class="text-gray-700">{{ dirtyRows.length }}</strong>
          unsaved {{ dirtyRows.length === 1 ? 'change' : 'changes' }}.
        </p>
        <div class="flex items-center gap-2">
          <BaseButton variant="secondary" :disabled="saving" @click="discard">Discard</BaseButton>
          <BaseButton variant="primary" :disabled="saving" @click="saveAll">
            {{ saving ? 'Saving…' : `Save changes (${dirtyRows.length})` }}
          </BaseButton>
        </div>
      </div>
    </div>

    <ConfirmDialog :show="pendingDelete !== null" title="Delete meal type"
      :message="`Delete the meal type “${pendingDelete?.name}”? Meals already using it keep their type.`"
      confirm-text="Delete" :loading="deleting" @confirm="confirmDelete" @cancel="pendingDelete = null" />
  </div>
</template>

<style scoped>
.dirty-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #b45309;
  background: #fef3c7;
  border-radius: 999px;
  padding: 1px 6px;
}
</style>
